import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { findDataset, scriptMaxScore, } from '@stem/lab-schema/runtime';
import { gradeLocally, initialState, labReducer, nextStepId, } from './state';
import { resolveStageWidget } from './registry';
import { NarrationPlayer } from './widgets/NarrationPlayer';
import { HintTutor, ProgressMeter } from './widgets/ProgressMeter';
import { CheckPanel, unhandledChecks } from './CheckPanel';
import { playChime } from './chime';
import { uuidv7 } from './uuidv7';
/**
 * The interpreter.
 *
 * One component reads a `LabScript` and renders it, for every lab the platform
 * will ever have. There is no per-lab code — 22 laboratories cannot be
 * hand-coded, and the moment one of them needs a special case in here, the DSL
 * has a gap that should be filled in the DSL.
 *
 * What it owns: the reducer, the step sequence, grading-for-feedback, and event
 * emission. What it deliberately does not own: networking. It emits `LabEvent`s
 * and calls `onStateChange`, and the host decides whether that means a POST, an
 * IndexedDB write, or nothing at all — which is what lets the same component
 * run in the preview harness, in a test, and offline.
 */
export function LabRuntime({ script, tier, initial, resolveAsset, readOnly = false, onEvent, onStateChange, onStepGraded, onComplete, }) {
    const [state, rawDispatch] = useReducer(labReducer, undefined, () => initial ?? initialState(script, tier));
    const [submitting, setSubmitting] = useState(false);
    const emitted = useRef(new Set());
    // The tier can change mid-run — a learner who was on 2G at school is on wifi
    // at home, or has just used the quality switch. The run follows them rather
    // than pinning them to the weakest device they ever used.
    useEffect(() => {
        if (state.tier !== tier)
            rawDispatch({ type: 'setTier', tier });
    }, [tier, state.tier]);
    useEffect(() => {
        onStateChange?.(state);
    }, [state, onStateChange]);
    const step = script.steps.find(candidate => candidate.id === state.currentStepId) ?? script.steps[0];
    const stepState = state.steps[step.id];
    const emit = useCallback((event) => {
        onEvent?.({
            ...event,
            id: uuidv7(),
            tier: state.tier,
            occurredAt: new Date().toISOString(),
        });
    }, [onEvent, state.tier]);
    // One `step_started` per step per run. Without the guard, every re-render
    // that changed the current step would produce another, and the funnel would
    // report a step "started" four times by one learner.
    useEffect(() => {
        if (emitted.current.has(step.id))
            return;
        emitted.current.add(step.id);
        emit({
            kind: 'step_started',
            stepId: step.id,
            attempt: (stepState?.attempts ?? 0) + 1,
            earned: 0,
            possible: stepState?.possible ?? 0,
            payload: { title: step.title },
        });
    }, [step.id, step.title, stepState?.attempts, stepState?.possible, emit]);
    const dispatch = useCallback((action) => {
        if (readOnly)
            return;
        rawDispatch(action);
    }, [readOnly]);
    const dataset = useCallback((datasetId) => findDataset(script, datasetId), [script]);
    const assets = useCallback((assetId) => (resolveAsset ? resolveAsset(assetId) : null), [resolveAsset]);
    const submit = useCallback(() => {
        if (readOnly || submitting)
            return;
        setSubmitting(true);
        const result = gradeLocally(script, state, step.id);
        const at = Date.now();
        rawDispatch({ type: 'graded', stepId: step.id, result, at });
        onStepGraded?.(result);
        playChime(result.passed ? 'correct' : 'incorrect');
        const attempt = (stepState?.attempts ?? 0) + 1;
        // One event per check — this is what makes "most learners get question three
        // wrong" answerable — and then exactly one for the step.
        for (const check of result.results) {
            emit({
                kind: 'check_attempted',
                stepId: step.id,
                checkId: check.checkId,
                attempt,
                correct: check.correct,
                earned: check.earned,
                possible: check.possible,
                payload: { wrong: check.wrong },
            });
        }
        /*
         * The step-level event, emitted on **every** attempt rather than only on a
         * pass, and distinguished by carrying no `checkId`.
         *
         * That distinction is load-bearing for the host: it posts the submission to
         * the server on this event alone. Keying off `check_attempted` instead
         * would fire one identical POST per check — five for a step with five
         * questions — and each would count as another attempt against a step that
         * limits them.
         */
        emit({
            kind: result.passed ? 'step_completed' : 'check_attempted',
            stepId: step.id,
            attempt,
            correct: result.passed,
            earned: result.earned,
            possible: result.possible,
            durationMs: stepState?.elapsedMs,
            payload: { checks: result.results.length },
        });
        setSubmitting(false);
    }, [readOnly, submitting, script, state, step.id, stepState, emit, onStepGraded]);
    const goTo = useCallback((stepId) => {
        rawDispatch({ type: 'enterStep', stepId, at: Date.now() });
    }, []);
    const advance = useCallback(() => {
        const next = nextStepId(script, step.id);
        if (next) {
            goTo(next);
            return;
        }
        const at = Date.now();
        rawDispatch({ type: 'complete', at });
        const totals = Object.values(state.steps).reduce((sum, entry) => ({ earned: sum.earned + entry.earned, possible: sum.possible + entry.possible }), { earned: 0, possible: 0 });
        onComplete?.(totals);
    }, [script, step.id, goTo, state.steps, onComplete]);
    const Widget = resolveStageWidget(step.stage.widget);
    const leftover = useMemo(() => unhandledChecks(step, step.stage.widget), [step]);
    const maxAttemptsReached = step.scoring.maxAttempts > 0 && (stepState?.attempts ?? 0) >= step.scoring.maxAttempts;
    const passed = stepState?.status === 'passed';
    const locked = readOnly || passed || maxAttemptsReached;
    const tone = stepState?.results ? (passed ? 'correct' : 'incorrect') : undefined;
    const message = stepState?.results
        ? passed
            ? step.feedback.correct
            : step.feedback.incorrect
        : undefined;
    return (_jsxs("div", { className: `lab-runtime lab-tier-${state.tier.replace('.', '-')}`, "data-tier": state.tier, children: [_jsx(ProgressMeter, { script: script, state: state, showMeter: script.layers.progress.showMeter, showStepList: script.layers.progress.showStepList, onSelectStep: goTo }), _jsxs("main", { className: "lab-main", children: [_jsxs("header", { className: "lab-step-header", children: [_jsxs("p", { className: "lab-step-header__eyebrow", children: ["Step ", script.steps.findIndex(candidate => candidate.id === step.id) + 1, " of", ' ', script.steps.length] }), _jsx("h2", { className: "lab-step-header__title", children: step.title }), _jsx("p", { className: "lab-step-header__objective", children: step.objective })] }), step.actions.length > 0 && (_jsx("ul", { className: "lab-actions", "aria-label": "What to do", children: step.actions.map((action, index) => (_jsxs("li", { children: [_jsx("span", { className: "lab-actions__kind", "aria-hidden": "true", children: ACTION_ICONS[action.kind] }), action.label] }, index))) })), _jsx(NarrationPlayer, { step: step, tier: state.tier, transcriptAlways: script.layers.audio.transcriptAlways, volume: script.layers.audio.defaultVolume, src: assets(step.narration.assetId) }), _jsx("section", { className: "lab-stage", "aria-label": "Laboratory", children: Widget ? (_jsx(Widget, { stage: step.stage, script: script, step: step, state: stepState, tier: state.tier, dispatch: dispatch, resolveAsset: assets, disabled: locked, dataset: dataset })) : (_jsxs("p", { className: "lab-empty", children: ["This step asks for the \u201C", step.stage.widget, "\u201D widget, which this build does not register."] })) }), _jsx(CheckPanel, { step: step, state: stepState, checks: leftover, disabled: locked, dispatch: dispatch }), script.layers.progress.showHintTutor && (_jsx(HintTutor, { hints: step.feedback.hints, revealed: stepState?.hintIndex ?? 0, message: message, tone: tone, onRequest: () => {
                            dispatch({ type: 'showHint', stepId: step.id });
                            emit({
                                kind: 'hint_shown',
                                stepId: step.id,
                                attempt: (stepState?.attempts ?? 0) + 1,
                                earned: 0,
                                possible: 0,
                                payload: { index: (stepState?.hintIndex ?? 0) + 1 },
                            });
                        } })), _jsxs("footer", { className: "lab-footer", children: [_jsxs("div", { className: "lab-footer__status", children: [stepState?.results && (_jsxs("span", { className: passed ? 'lab-badge is-correct' : 'lab-badge is-wrong', children: [stepState.earned, " / ", stepState.possible, " points"] })), step.scoring.maxAttempts > 0 && (_jsxs("span", { className: "lab-meta", children: ["Attempt ", Math.min(stepState?.attempts ?? 0, step.scoring.maxAttempts), " of", ' ', step.scoring.maxAttempts] }))] }), _jsxs("div", { className: "lab-footer__actions", children: [!readOnly && !passed && (_jsx("button", { type: "button", className: "lab-button lab-button--ghost", onClick: () => dispatch({ type: 'resetStep', stepId: step.id }), children: "Start again" })), !passed && !maxAttemptsReached && !readOnly && (_jsx("button", { type: "button", className: "lab-button", disabled: submitting, onClick: submit, children: "Check my work" })), (passed || maxAttemptsReached || readOnly) && (_jsx("button", { type: "button", className: "lab-button", onClick: advance, children: nextStepId(script, step.id) ? 'Next step' : 'Finish the lab' }))] })] }), maxAttemptsReached && !passed && (_jsxs("p", { className: "lab-hint", children: ["You have used all ", step.scoring.maxAttempts, " attempts on this step. You can move on \u2014 your teacher will see where you got stuck."] })), state.completedAt && (_jsxs("section", { className: "lab-complete", "aria-live": "polite", children: [_jsx("h3", { children: "Lab complete" }), _jsxs("p", { children: ["You scored", ' ', _jsxs("strong", { children: [Object.values(state.steps).reduce((sum, entry) => sum + entry.earned, 0), " out of", ' ', scriptMaxScore(script)] }), "."] })] }))] })] }));
}
const ACTION_ICONS = {
    click: '👆',
    drag: '✊',
    drop: '🎯',
    select: '☑️',
    input: '⌨️',
    hover: '🔍',
};
//# sourceMappingURL=LabRuntime.js.map